import React from "react";

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: React.ReactNode;
  fullWidth?: boolean;
  options?: Array<{ value: string | number; label: string }>;
}

export function Select({
  label,
  error,
  helperText,
  icon,
  fullWidth = true,
  options,
  className = "",
  id,
  children,
  ...props
}: SelectProps) {
  const generatedId = React.useId();
  const selectId = id || `select-${generatedId}`;

  return (
    <div className={`${fullWidth ? "w-full" : ""}`}>
      {label && (
        <label
          htmlFor={selectId}
          className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700"
        >
          {icon && <span className="text-primary-600">{icon}</span>}
          <span>{label}</span>
          {props.required && <span className="text-rose-500">*</span>}
        </label>
      )}
      <select
        id={selectId}
        className={`
          w-full px-4 py-3 text-sm font-semibold
          bg-white border rounded-xl
          transition-all duration-200
          focus:outline-none focus:ring-4
          disabled:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-500
          appearance-none
          cursor-pointer
          ${
            error
              ? "border-rose-300 focus:border-rose-500 focus:ring-rose-100"
              : "border-slate-200 hover:border-primary-300 focus:border-primary-400 focus:ring-primary-100"
          }
          ${className}
        `
          .trim()
          .replace(/\s+/g, " ")}
        aria-invalid={!!error}
        aria-describedby={
          error ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined
        }
        style={{
          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
          backgroundPosition: "right 0.5rem center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "1.5em 1.5em",
          paddingRight: "2.5rem",
        }}
        {...props}
      >
        {options
          ? options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))
          : children}
      </select>
      {error && (
        <p
          id={`${selectId}-error`}
          className="mt-2 flex items-center gap-1.5 text-sm font-medium text-rose-600"
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span>{error}</span>
        </p>
      )}
      {helperText && !error && (
        <p id={`${selectId}-helper`} className="mt-2 text-sm text-slate-500">
          {helperText}
        </p>
      )}
    </div>
  );
}
